import { create } from 'zustand';
import { supabase } from '../lib/supabase';

interface AnalysisData {
  id: string;
  measurement_date: string;
  name: string;
  distance: number;
  data: {
    speed?: number;
    pitch?: number;
    stride?: number;
    contactTime?: number;
    flightTime?: number;
    predicted100m?: number | string;
  };
  comment?: string;
  created_at: string;
  updated_at: string;
}

interface AnalysisDataState {
  results: AnalysisData[];
  isLoading: boolean;
  error: string | null;
  fetchResults: () => Promise<void>;
  addResult: (data: Omit<AnalysisData, 'id' | 'created_at' | 'updated_at'>) => Promise<void>;
  deleteResult: (id: string) => Promise<void>;
  getResultCount: () => Promise<number>;
  getOldestResults: (count: number) => Promise<AnalysisData[]>;
  isOverLimit: boolean;
  checkLimit: () => Promise<boolean>;
}

const MAX_RESULTS_PER_USER = 20;

export const useAnalysisDataStore = create<AnalysisDataState>((set, get) => ({
  results: [],
  isLoading: false,
  error: null,
  isOverLimit: false,

  fetchResults: async () => {
    try {
      set({ isLoading: true, error: null });

      const { data: { session }, error: sessionError } = await supabase.auth.getSession();
      if (sessionError) throw new Error('セッションの取得に失敗しました');
      if (!session?.user) {
        set({ results: [], isOverLimit: false });
        return;
      }

      const { data, error } = await supabase
        .from('analysis_data')
        .select('*')
        .eq('user_id', session.user.id)
        .order('measurement_date', { ascending: false });

      if (error) throw error;

      set({ 
        results: data || [],
        isOverLimit: (data?.length || 0) >= MAX_RESULTS_PER_USER
      });
      
      return;
    } catch (err) {
      console.error('Error fetching results:', err);
      set({ error: err instanceof Error ? err.message : 'データの取得に失敗しました' });
    } finally {
      set({ isLoading: false });
    }
  },

  getResultCount: async () => {
    try {
      const { data: { session }, error: sessionError } = await supabase.auth.getSession();
      if (sessionError) return 0;
      if (!session?.user) return 0;

      const { count, error } = await supabase
        .from('analysis_data')
        .select('*', { count: 'exact', head: true })
        .eq('user_id', session.user.id);

      if (error) throw error;
      
      return count || 0;
    } catch (err) {
      console.error('Error getting result count:', err);
      return 0;
    }
  },

  getOldestResults: async (count: number) => {
    try {
      const { data: { session }, error: sessionError } = await supabase.auth.getSession();
      if (sessionError || !session?.user) return [];

      const { data, error } = await supabase
        .from('analysis_data')
        .select('*')
        .eq('user_id', session.user.id)
        .order('created_at', { ascending: true })
        .limit(count);

      if (error) throw error;
      
      return data || [];
    } catch (err) {
      console.error('Error getting oldest results:', err);
      return [];
    }
  },

  checkLimit: async () => {
    const count = await get().getResultCount();
    const isOverLimit = count >= MAX_RESULTS_PER_USER;
    set({ isOverLimit });
    return isOverLimit;
  },

  addResult: async (data) => {
    try {
      set({ isLoading: true, error: null });

      const { data: { session }, error: sessionError } = await supabase.auth.getSession();
      if (sessionError) throw new Error('セッションの取得に失敗しました');
      if (!session?.user) throw new Error('認証されていません');

      // Check if user has reached the limit
      const count = await get().getResultCount();
      if (count >= MAX_RESULTS_PER_USER) {
        set({ isOverLimit: true });
        throw new Error(`データの保存数が上限（${MAX_RESULTS_PER_USER}件）に達しています。古いデータを削除してから再度お試しください。`);
      }

      const { data: newData, error } = await supabase
        .from('analysis_data')
        .insert({
          user_id: session.user.id,
          measurement_date: data.measurement_date,
          name: data.name,
          distance: data.distance,
          data: data.data || {},
          comment: data.comment
        })
        .select()
        .single();

      if (error) throw error;
      if (!newData) throw new Error('データの保存に失敗しました');

      set(state => ({
        results: [newData, ...state.results],
        isOverLimit: state.results.length + 1 >= MAX_RESULTS_PER_USER
      }));
      
      // Check if we're at the limit after adding
      await get().checkLimit();
      
    } catch (err) {
      console.error('Error adding result:', err);
      set({ error: err instanceof Error ? err.message : 'データの保存に失敗しました' });
      throw err;
    } finally {
      set({ isLoading: false });
    }
  },

  deleteResult: async (id) => {
    try {
      set({ isLoading: true, error: null });

      const { data: { session }, error: sessionError } = await supabase.auth.getSession();
      if (sessionError) throw new Error('セッションの取得に失敗しました');
      if (!session?.user) throw new Error('認証されていません');

      const { error } = await supabase
        .from('analysis_data')
        .delete()
        .eq('id', id)
        .eq('user_id', session.user.id);

      if (error) throw error;

      set(state => {
        const newResults = state.results.filter(result => result.id !== id);
        return {
          results: newResults,
          isOverLimit: newResults.length >= MAX_RESULTS_PER_USER
        };
      });
      
      // Check if we're still at the limit after deleting
      await get().checkLimit();
      
    } catch (err) {
      console.error('Error deleting result:', err);
      set({ error: err instanceof Error ? err.message : 'データの削除に失敗しました' });
      throw err;
    } finally {
      set({ isLoading: false });
    }
  },
}));